/** Inicio v2: saludo, banner de pendientes y accesos (design.md §22.3). Los ajustes viven en Más (/m/more). */
import { Link } from 'react-router-dom';
import { MobileLayout } from '@/components/MobileLayout';
import { HomeCard } from '@/components/MobileCard';
import { MobileIcon } from '@/components/MobileIcon';
import { homeCards, visibleCards } from '@/config/mobileMenu';
import { useMobileTasksQuery } from '@/hooks/useMobileTasks';
import { useMobileAuth } from '@/state/MobileAuthContext';

const cardColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  panel: 'info',
  'requirement-new': 'success',
  requirements: 'primary',
  'quote-generator': 'secondary',
  budgets: 'info',
  clients: 'primary',
  tasks: 'warning',
};

/** Texto de frescura del dashboard a partir del sello de la ultima consulta. */
export function freshnessLabel(updatedAt: number | undefined, now: number = Date.now()): string | null {
  if (!updatedAt) return null;
  const elapsed = Math.max(0, now - updatedAt);
  if (elapsed < 10_000) return 'Actualizado ahora';
  if (elapsed < 60_000) return 'Actualizado hace instantes';
  if (elapsed < 3_600_000) return `Actualizado hace ${Math.floor(elapsed / 60_000)} min`;
  return `Actualizado hace ${Math.floor(elapsed / 3_600_000)} h`;
}

function pendingLabel(count: number): string {
  return count > 1 ? `Tienes ${count} pendientes` : `Tienes ${count} pendiente`;
}

/** Banner de tareas pendientes: con una sola abre el detalle, con varias abre la lista. */
function PendingBanner({ count, firstTaskId }: { count: number; firstTaskId: string | null }) {
  const isSingle = count === 1 && firstTaskId !== null && firstTaskId !== '';
  const to = isSingle ? `/m/tasks/${firstTaskId}` : '/m/tasks';
  const subtitle = isSingle ? 'Toca para ver el detalle' : 'Toca para ver tus tareas';
  return (
    <Link
      to={to}
      className="mb-4 flex items-center gap-3 rounded-card bg-warning-strong p-4 shadow-card transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20 text-white">
        <MobileIcon name="warning" className="h-6 w-6" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words text-base font-semibold text-white">
          {pendingLabel(count)}
        </span>
        <span className="block break-words text-sm text-white">{subtitle}</span>
      </span>
      <MobileIcon name="chevron_right" className="h-6 w-6 shrink-0 text-white" />
    </Link>
  );
}

export function HomePage() {
  const { user, hasPermission } = useMobileAuth();
  const pendingQuery = useMobileTasksQuery('open');
  const pendingItems = pendingQuery.data ?? [];
  const pendingCount = pendingItems.length;
  const firstTaskId = pendingItems[0]?.id ?? null;

  const visible = visibleCards(homeCards, hasPermission);
  const hasCards = visible.length > 0;
  const firstName = user?.full_name.trim().split(/\s+/)[0] ?? '';
  const greeting = firstName ? `Hola, ${firstName}` : 'Inicio';
  const freshness = freshnessLabel(pendingQuery.dataUpdatedAt);

  return (
    <MobileLayout title={greeting} subtitle={freshness ?? undefined}>
      {pendingCount > 0 && <PendingBanner count={pendingCount} firstTaskId={firstTaskId} />}

      {hasCards && (
        <section className="mt-6">
          <h2 className="mb-3 px-1 text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Accesos
          </h2>
          <div className="space-y-3">
            {visible.map((card) => (
              <Link key={card.key} to={card.path} aria-label={card.label} className="block">
                <HomeCard
                  title={card.label}
                  description={card.description}
                  icon={card.icon}
                  iconColor={cardColors[card.key] ?? 'primary'}
                  badge={card.key === 'tasks' && pendingCount > 0 ? pendingCount : undefined}
                />
              </Link>
            ))}
          </div>
        </section>
      )}

      {!hasCards && (
        <div className="mt-6 rounded-card bg-white p-6 text-center shadow-card">
          <div className="mb-3 flex justify-center text-text-tertiary">
            <MobileIcon name="lock_person" className="h-12 w-12" />
          </div>
          <p className="mb-1 text-base font-semibold text-text-primary">
            Sin accesos comerciales
          </p>
          <p className="text-sm text-text-secondary">
            Pide a tu administrador el rol correspondiente.
          </p>
        </div>
      )}
    </MobileLayout>
  );
}
